<script setup lang="ts">
import {
  computed,
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import isEqual from 'lodash/isEqual'
import { dateFormat } from '@/components/helpers/formats'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import {
  parseAmountToPence,
  penceToAmountInput,
} from '@/helpers/parseAmountToPence'
import { donationIsoToUkDate, ukTodayIso } from '@/helpers/recurring/dates'
import { summariseRecurringGiftAid } from '@/helpers/recurring/giftAidOverview'
import { finalScheduledOccurrenceDate } from '@/helpers/recurring/recurrence'
import { isDonationGiftAidDeclared } from '@/helpers/giftAidDeclared'
import debug from '@/helpers/debug'
import {
  DONATION_ANALYTICS_EVENTS,
  trackDonationAnalytics,
} from '@/helpers/donationsAnalytics'
import { useRecurringDonationModal } from '@/composables/useRecurringDonationModal'
import { useRecurringDonations } from '@/composables/useRecurringDonations'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import { useDonationDetailModal } from '@/composables/useDonationDetailModal'
import { useDashboardToast } from '@/composables/useDashboardToast'
import DonationGiftAidChoice from '@/components/dashboard/donations/DonationGiftAidChoice.vue'
import RecurringScheduleFields from '@/components/dashboard/donations/RecurringScheduleFields.vue'
import DonationCharityHeader from '@/components/dashboard/ui/DonationCharityHeader.vue'
import DashboardFormField from '@/components/dashboard/ui/DashboardFormField.vue'
import DetailSurface from '@/components/dashboard/modals/DetailSurface.vue'
import DonationDetailPanel from '@/components/dashboard/modals/DonationDetailPanel.vue'
import type { DonorDonation } from '@/types/donorFinancialYear'
import DonationListRow from '@/components/dashboard/ui/DonationListRow.vue'
import type {
  RecurringApplyScope,
  RecurringEndCondition,
  RecurringEndConditionType,
  RecurringFrequency,
} from '@/types/recurringDonation'

/** Newest gifts shown when Donation history is opened, and on each Show more. */
const HISTORY_PAGE_SIZE = 20

type PanelView = 'overview' | 'edit' | 'skip' | 'stop' | 'restart' | 'delete'

const props = withDefaults(
  defineProps<{
    /**
     * Drawn inside the donation detail modal. That modal owns the backdrop,
     * the Back control and Close.
     */
    inline?: boolean
    seriesId?: string
  }>(),
  { inline: false }
)

const emit = defineEmits<{
  openHistory: [donation: DonorDonation]
  seriesDeleted: [seriesId: string]
  busy: [busy: boolean]
  labelledBy: [id: string]
}>()

const { t } = useI18n()
const { showToast } = useDashboardToast()
const { isOpen, selectedSeriesId, closeRecurringDonationModal } =
  useRecurringDonationModal()
const {
  seriesById,
  updateRecurringDonation,
  stopRecurringDonation,
  restartRecurringDonation,
  skipRecurringNextOccurrence,
  deleteRecurringDonation,
} = useRecurringDonations()
const { manualDonations } = useManualGivingDonations()
const { selectedDonation, closeDonationDetailModal } = useDonationDetailModal()

const activeSeriesId = computed(() =>
  props.inline ? props.seriesId : (selectedSeriesId.value ?? undefined)
)
const surfaceOpen = computed(() =>
  props.inline ? Boolean(props.seriesId) : isOpen.value
)
const series = computed(() => seriesById(activeSeriesId.value))
const panelView = ref<PanelView>('overview')
const historyVisibleCount = ref(HISTORY_PAGE_SIZE)
// Collapsed until the donor asks for the generated donations.
const historyOpen = ref(false)
const historyDetails = ref<HTMLDetailsElement | null>(null)
const acceptHistoryToggle = ref(true)
const overviewScrollTop = ref(0)

// Leaving this view can make the browser close the disclosure. Ignore that,
// and put the saved state back when the donor returns.
const panelScroller = () =>
  historyDetails.value?.closest<HTMLElement>('.sa-donation-detail-panel__scroll') ??
  null

onMounted(() => {
  const scroller = panelScroller()
  if (!scroller) return
  scroller.addEventListener('scroll', () => {
    // Hiding the view can emit a scroll back to the top. Ignore that.
    if (!scroller.isConnected) return
    overviewScrollTop.value = scroller.scrollTop
  })
})

onDeactivated(() => {
  acceptHistoryToggle.value = false
})
onActivated(() => {
  acceptHistoryToggle.value = true
  void nextTick(() => {
    const top = overviewScrollTop.value
    if (historyDetails.value) historyDetails.value.open = historyOpen.value
    const scroller = panelScroller()
    if (scroller) scroller.scrollTop = top
  })
})

const isSubmitting = ref(false)
// A generated donation is shown in this same modal, not a second one.
const historyDonationId = ref<string | null>(null)
// Kept off the template ref. Reading the panel instance during render retriggers
// the modal, because Vue assigns that ref while the same render is in progress.
const historyDetailBusy = ref(false)

const amount = ref('')
const frequency = ref<RecurringFrequency>('monthly')
const nextDate = ref('')
const endType = ref<RecurringEndConditionType>('until_stopped')
const endDate = ref('')
const occurrenceCount = ref('')
const giftAidDeclared = ref<boolean | null>(null)
const notes = ref('')
const applyScope = ref<RecurringApplyScope>('future')

const restartAmount = ref('')
const restartFrequency = ref<RecurringFrequency>('monthly')
const restartNextDate = ref('')
const restartEndType = ref<RecurringEndConditionType>('until_stopped')
const restartEndDate = ref('')
const restartOccurrenceCount = ref('')
const restartGiftAid = ref<boolean | null>(null)
const restartNotes = ref('')

const fieldErrors = reactive({
  amount: '',
  nextDate: '',
  endDate: '',
  occurrenceCount: '',
  giftAid: '',
  restartAmount: '',
  restartNextDate: '',
  restartEndDate: '',
  restartOccurrenceCount: '',
  restartGiftAid: '',
})

const frequencyLabelKey: Record<RecurringFrequency, string> = {
  weekly: 'Dashboard.Giving.AddDonationModal.FrequencyWeekly',
  monthly: 'Dashboard.Giving.AddDonationModal.FrequencyMonthly',
  quarterly: 'Dashboard.Giving.AddDonationModal.FrequencyQuarterly',
  annually: 'Dashboard.Giving.AddDonationModal.FrequencyAnnually',
}

const statusLabelKey = computed(() => {
  if (series.value?.status === 'stopped') {
    return 'Dashboard.RecurringDonationModal.StatusStopped'
  }
  if (series.value?.status === 'completed') {
    return 'Dashboard.RecurringDonationModal.StatusCompleted'
  }
  return 'Dashboard.RecurringDonationModal.StatusActive'
})

const populateFromSeries = () => {
  if (!series.value) return
  amount.value = penceToAmountInput(series.value.amountPence)
  frequency.value = series.value.frequency
  nextDate.value = series.value.nextDonationDate ?? ukTodayIso()
  endType.value = series.value.endCondition.type
  endDate.value =
    series.value.endCondition.type === 'on_date'
      ? series.value.endCondition.endDate
      : ''
  occurrenceCount.value =
    series.value.endCondition.type === 'after_occurrences'
      ? String(series.value.endCondition.occurrenceCount)
      : ''
  giftAidDeclared.value = series.value.giftAidDeclared
  notes.value = series.value.notes ?? ''
  applyScope.value = 'future'
}

const populateRestartForm = () => {
  populateFromSeries()
  restartAmount.value = amount.value
  restartFrequency.value = frequency.value
  // A stopped series keeps its old next date; a restart cannot use a past one.
  const today = ukTodayIso()
  const storedNextDate = series.value?.nextDonationDate
  restartNextDate.value =
    storedNextDate && storedNextDate >= today ? storedNextDate : today
  restartEndType.value = endType.value
  // Nor can it end before it restarts: an end date that has passed is cleared.
  restartEndDate.value =
    endDate.value >= restartNextDate.value ? endDate.value : ''
  restartOccurrenceCount.value = occurrenceCount.value
  restartGiftAid.value = giftAidDeclared.value
  restartNotes.value = notes.value
}

watch(
  () => [surfaceOpen.value, series.value?.id],
  ([open]) => {
    historyDonationId.value = null
    historyDetailBusy.value = false
    overviewScrollTop.value = 0
    if (!open) {
      panelView.value = 'overview'
      historyVisibleCount.value = HISTORY_PAGE_SIZE
      historyOpen.value = false
      isSubmitting.value = false
      return
    }
    populateFromSeries()
    panelView.value = 'overview'
    historyVisibleCount.value = HISTORY_PAGE_SIZE
    historyOpen.value = false
  }
)

const historyDonation = computed(() => {
  if (!historyDonationId.value) return null
  return (
    manualDonations.value.find((row) => row.id === historyDonationId.value) ??
    null
  )
})

// Deleting the row unmounts the panel before it can report that it is idle.
// Without this, Close stays disabled on the schedule underneath. Return the way
// Back does, so scroll and focus come back to the history.
watch(historyDonation, (donation) => {
  if (donation || !historyDonationId.value) return
  historyDetailBusy.value = false
  void backFromHistoryDonation()
})

const recurringModalBody = () =>
  document.querySelector<HTMLElement>('.sa-recurring-modal .sa-modal__body')

const historyRows = computed(() => {
  if (!series.value) return []
  return [...manualDonations.value]
    .filter((row) => row.recurringSeriesId === series.value?.id)
    .sort((a, b) => b.time.localeCompare(a.time))
})

const onHistoryToggle = (event: Event) => {
  const details = event.currentTarget
  if (!(details instanceof HTMLDetailsElement)) return
  // Moving the view out of the modal can close the disclosure. That is not a click.
  if (!acceptHistoryToggle.value || !details.isConnected) return
  if (historyOpen.value === details.open) return
  historyOpen.value = details.open
  debug('Recurring donation history disclosure toggled', {
    seriesId: series.value?.id,
    open: details.open,
    count: historyRows.value.length,
  })
}

// `toggle` on `<details>` clashes with the DOM `toggle` attribute in vue-tsc,
// so the listener is attached here rather than with `@toggle`.
watch(historyDetails, (details, previous) => {
  previous?.removeEventListener('toggle', onHistoryToggle)
  details?.addEventListener('toggle', onHistoryToggle)
})

const visibleHistory = computed(() =>
  historyRows.value.slice(0, historyVisibleCount.value)
)

const hasMoreHistory = computed(
  () => historyRows.value.length > historyVisibleCount.value
)

const showMoreHistory = () => {
  historyVisibleCount.value += HISTORY_PAGE_SIZE
  debug('Showing more recurring donation history', {
    seriesId: series.value?.id,
    visible: Math.min(historyVisibleCount.value, historyRows.value.length),
    total: historyRows.value.length,
  })
}

const lastDonationDate = computed(() => historyRows.value[0]?.time ?? null)

const finalDonationDate = computed(() =>
  series.value ? finalScheduledOccurrenceDate(series.value) : null
)

const endsValue = computed(() => {
  if (!series.value) return ''
  if (series.value.endCondition.type === 'on_date') {
    return dateFormat(series.value.endCondition.endDate)
  }
  if (series.value.endCondition.type === 'after_occurrences') {
    return t('Dashboard.RecurringDonationModal.OccurrenceLimit', {
      count: series.value.endCondition.occurrenceCount,
    })
  }
  return t('Dashboard.Giving.AddDonationModal.EndUntilStopped')
})

const notesDisplay = computed(() => {
  const notes = series.value?.notes?.trim()
  return notes || t('Dashboard.RecurringDonationModal.NotesEmpty')
})

const giftAidOverview = computed(() => {
  // Series Gift Aid is the default for future rows; history can differ.
  if (!series.value) {
    return { varies: false, exceptionCount: 0 }
  }
  return summariseRecurringGiftAid(
    series.value.giftAidDeclared,
    historyRows.value.map((row) => isDonationGiftAidDeclared(row))
  )
})

const giftAidFactLabel = computed(() =>
  series.value?.giftAidDeclared
    ? t('Dashboard.RecurringDonationModal.GiftAidYes')
    : t('Dashboard.RecurringDonationModal.GiftAidNo')
)

const giftAidExceptionLabel = computed(() => {
  const count = giftAidOverview.value.exceptionCount
  if (count < 1) return ''
  // The primary value is the schedule default; this line explains history exceptions.
  if (series.value?.giftAidDeclared) {
    return count === 1
      ? t('Dashboard.RecurringDonationModal.GiftAidExceptionExcludedOne')
      : t('Dashboard.RecurringDonationModal.GiftAidExceptionExcludedMany', {
          count,
        })
  }
  return count === 1
    ? t('Dashboard.RecurringDonationModal.GiftAidExceptionIncludedOne')
    : t('Dashboard.RecurringDonationModal.GiftAidExceptionIncludedMany', {
        count,
      })
})

const historyCountLabel = computed(() => {
  const count = historyRows.value.length
  if (count === 1) return t('Dashboard.RecurringDonationModal.HistoryCountOne')
  return t('Dashboard.RecurringDonationModal.HistoryCountMany', { count })
})

// Trimmed on both sides: the API stores notes as sent, the form trims them.
const notesChanged = computed(
  () => notes.value.trim() !== (series.value?.notes?.trim() ?? '')
)

const valuesChanged = computed(() => {
  if (!series.value) return false
  const amountPence = parseAmountToPence(amount.value)
  return (
    amountPence !== series.value.amountPence ||
    giftAidDeclared.value !== series.value.giftAidDeclared ||
    notesChanged.value
  )
})

const buildEndCondition = (
  type: RecurringEndConditionType,
  dateValue: string,
  countValue: string,
  dateErrorKey: 'endDate' | 'restartEndDate',
  countErrorKey: 'occurrenceCount' | 'restartOccurrenceCount'
): RecurringEndCondition | null => {
  if (type === 'until_stopped') return { type: 'until_stopped' }
  if (type === 'on_date') {
    if (!dateValue) {
      fieldErrors[dateErrorKey] = t(
        'Dashboard.Giving.AddDonationModal.Toast.MissingEndDate'
      )
      return null
    }
    return { type: 'on_date', endDate: dateValue }
  }
  const count = Number.parseInt(countValue, 10)
  if (!Number.isInteger(count) || count < 1) {
    fieldErrors[countErrorKey] = t(
      'Dashboard.Giving.AddDonationModal.Toast.MissingOccurrenceCount'
    )
    return null
  }
  return { type: 'after_occurrences', occurrenceCount: count }
}

const deleteCount = computed(() => historyRows.value.length)

const deleteIntro = computed(() => {
  const count = deleteCount.value
  if (count === 0) return t('Dashboard.RecurringDonationModal.DeleteIntroEmpty')
  if (count === 1) return t('Dashboard.RecurringDonationModal.DeleteIntroOne')
  return t('Dashboard.RecurringDonationModal.DeleteIntro', { count })
})

const saveChanges = async () => {
  if (!series.value || isSubmitting.value) return
  fieldErrors.amount = ''
  fieldErrors.nextDate = ''
  fieldErrors.endDate = ''
  fieldErrors.occurrenceCount = ''
  fieldErrors.giftAid = ''

  const amountPence = parseAmountToPence(amount.value)
  if (amountPence === null) {
    fieldErrors.amount = t(
      'Dashboard.Giving.AddDonationModal.Toast.MissingAmount'
    )
    return
  }
  if (giftAidDeclared.value === null) {
    fieldErrors.giftAid = t(
      'Dashboard.Giving.AddDonationModal.Toast.MissingGiftAid'
    )
    return
  }
  if (series.value.status === 'active' && !nextDate.value) {
    fieldErrors.nextDate = t(
      'Dashboard.Giving.AddDonationModal.Toast.MissingDate'
    )
    return
  }
  const endCondition = buildEndCondition(
    endType.value,
    endDate.value,
    occurrenceCount.value,
    'endDate',
    'occurrenceCount'
  )
  if (!endCondition) return

  // Send only what the donor changed, so a form left open cannot rewrite the
  // schedule of a series that has moved on since it was loaded. A stopped or
  // completed series changes its schedule through restart.
  const current = series.value
  const frequencyChanged = frequency.value !== current.frequency
  const endChanged = !isEqual(endCondition, current.endCondition)
  const scheduleChanged =
    current.status === 'active' &&
    (frequencyChanged ||
      endChanged ||
      nextDate.value !== current.nextDonationDate)

  isSubmitting.value = true
  try {
    await updateRecurringDonation(current.id, {
      amountPence:
        amountPence !== current.amountPence ? amountPence : undefined,
      giftAidDeclared:
        giftAidDeclared.value !== current.giftAidDeclared
          ? giftAidDeclared.value
          : undefined,
      // An empty string clears the notes; omitted leaves them unchanged.
      notes: notesChanged.value ? notes.value.trim() : undefined,
      frequency:
        scheduleChanged && frequencyChanged ? frequency.value : undefined,
      // The API needs the next date with any schedule change.
      nextDonationDate: scheduleChanged ? nextDate.value : undefined,
      endCondition: scheduleChanged && endChanged ? endCondition : undefined,
      applyScope: valuesChanged.value ? applyScope.value : 'future',
    })
    trackDonationAnalytics(DONATION_ANALYTICS_EVENTS.RecurringUpdated, {
      frequency: frequency.value,
      end_condition: endCondition.type,
      apply_scope: valuesChanged.value ? applyScope.value : undefined,
    })
    showToast(t('Dashboard.RecurringDonationModal.Saved'))
    populateFromSeries()
    panelView.value = 'overview'
  } catch (error) {
    console.error('Failed to update recurring donation', error)
    showToast(t('Dashboard.RecurringDonationModal.SaveFailed'))
  } finally {
    isSubmitting.value = false
  }
}

const confirmSkip = async () => {
  if (!series.value || isSubmitting.value) return
  isSubmitting.value = true
  try {
    await skipRecurringNextOccurrence(series.value.id)
    trackDonationAnalytics(
      DONATION_ANALYTICS_EVENTS.RecurringOccurrenceSkipped,
      {
        frequency: series.value.frequency,
      }
    )
    showToast(t('Dashboard.RecurringDonationModal.Skipped'))
    panelView.value = 'overview'
    populateFromSeries()
  } catch (error) {
    console.error('Failed to skip next recurring donation', error)
    showToast(t('Dashboard.RecurringDonationModal.SkipFailed'))
  } finally {
    isSubmitting.value = false
  }
}

const confirmStop = async () => {
  if (!series.value || isSubmitting.value) return
  isSubmitting.value = true
  try {
    await stopRecurringDonation(series.value.id)
    trackDonationAnalytics(DONATION_ANALYTICS_EVENTS.RecurringStopped, {
      frequency: series.value.frequency,
      end_condition: series.value.endCondition.type,
    })
    showToast(t('Dashboard.RecurringDonationModal.StoppedToast'))
    panelView.value = 'overview'
    populateFromSeries()
  } catch (error) {
    console.error('Failed to stop recurring donation', error)
    showToast(t('Dashboard.RecurringDonationModal.StopFailed'))
  } finally {
    isSubmitting.value = false
  }
}

const confirmRestart = async () => {
  if (!series.value || isSubmitting.value) return
  fieldErrors.restartAmount = ''
  fieldErrors.restartNextDate = ''
  fieldErrors.restartEndDate = ''
  fieldErrors.restartOccurrenceCount = ''
  fieldErrors.restartGiftAid = ''

  const amountPence = parseAmountToPence(restartAmount.value)
  if (amountPence === null) {
    fieldErrors.restartAmount = t(
      'Dashboard.Giving.AddDonationModal.Toast.MissingAmount'
    )
    return
  }
  if (!restartNextDate.value) {
    fieldErrors.restartNextDate = t(
      'Dashboard.Giving.AddDonationModal.Toast.MissingDate'
    )
    return
  }
  if (restartNextDate.value < ukTodayIso()) {
    fieldErrors.restartNextDate = t(
      'Dashboard.RecurringDonationModal.NextDateInPast'
    )
    return
  }
  if (restartGiftAid.value === null) {
    fieldErrors.restartGiftAid = t(
      'Dashboard.Giving.AddDonationModal.Toast.MissingGiftAid'
    )
    return
  }
  const endCondition = buildEndCondition(
    restartEndType.value,
    restartEndDate.value,
    restartOccurrenceCount.value,
    'restartEndDate',
    'restartOccurrenceCount'
  )
  if (!endCondition) return

  isSubmitting.value = true
  try {
    await restartRecurringDonation(series.value.id, {
      amountPence,
      giftAidDeclared: restartGiftAid.value,
      // An empty string clears the notes.
      notes: restartNotes.value.trim(),
      frequency: restartFrequency.value,
      nextDonationDate: restartNextDate.value,
      endCondition,
    })
    trackDonationAnalytics(DONATION_ANALYTICS_EVENTS.RecurringRestarted, {
      frequency: restartFrequency.value,
      end_condition: endCondition.type,
    })
    showToast(t('Dashboard.RecurringDonationModal.RestartedToast'))
    panelView.value = 'overview'
    populateFromSeries()
  } catch (error) {
    console.error('Failed to restart recurring donation', error)
    showToast(t('Dashboard.RecurringDonationModal.RestartFailed'))
  } finally {
    isSubmitting.value = false
  }
}

const confirmDelete = async () => {
  if (!series.value || isSubmitting.value) return
  const seriesId = series.value.id
  const frequency = series.value.frequency
  const endCondition = series.value.endCondition.type
  const occurrenceCount = historyRows.value.length
  isSubmitting.value = true
  try {
    await deleteRecurringDonation(seriesId)
    trackDonationAnalytics(DONATION_ANALYTICS_EVENTS.RecurringDeleted, {
      frequency,
      end_condition: endCondition,
      occurrence_count: occurrenceCount,
    })
    showToast(t('Dashboard.RecurringDonationModal.Deleted'))
    if (props.inline) {
      // The host removes this schedule and any gift that belonged to it.
      emit('seriesDeleted', seriesId)
    } else {
      closeRecurringDonationModal()
      if (selectedDonation.value?.recurringSeriesId === seriesId) {
        closeDonationDetailModal()
      }
    }
  } catch (error) {
    console.error('Failed to delete recurring donation', error)
    showToast(t('Dashboard.RecurringDonationModal.DeleteFailed'))
  } finally {
    isSubmitting.value = false
  }
}

const startEdit = async () => {
  if (!series.value) return
  populateFromSeries()
  panelView.value = 'edit'
  debug('Opening recurring donation edit mode', {
    seriesId: series.value.id,
    status: series.value.status,
  })
  await nextTick()
  document.getElementById('recurring-manage-amount')?.focus()
}

const cancelEdit = () => {
  populateFromSeries()
  panelView.value = 'overview'
}

const openHistoryDonation = async (id: string) => {
  const donation = manualDonations.value.find((row) => row.id === id)
  if (!donation) return
  if (props.inline) {
    // The host pushes this gift onto the same modal. This view stays mounted.
    debug('Opening a generated donation from an inline recurring donation', {
      seriesId: series.value?.id,
      donationId: id,
    })
    emit('openHistory', donation)
    return
  }
  // Remember where the donor was in the schedule, including an open history.
  overviewScrollTop.value = recurringModalBody()?.scrollTop ?? 0
  historyDetailBusy.value = false
  historyDonationId.value = id
  debug('Opening a generated donation inside the recurring donation', {
    seriesId: series.value?.id,
    donationId: id,
    historyOpen: historyOpen.value,
  })
  await nextTick()
  document.getElementById('recurring-history-back')?.focus()
}

const backFromHistoryDonation = async () => {
  debug('Returning to the recurring donation from a generated donation', {
    seriesId: series.value?.id,
    donationId: historyDonationId.value,
  })
  const top = overviewScrollTop.value
  historyDonationId.value = null
  await nextTick()
  const body = recurringModalBody()
  if (body) body.scrollTop = top
  document.getElementById('recurring-history-heading')?.focus()
}

const startRestart = () => {
  populateRestartForm()
  panelView.value = 'restart'
}

const titleId = computed(() => {
  if (historyDonation.value) return 'donation-detail-title'
  if (panelView.value === 'restart') return 'recurring-restart-title'
  if (panelView.value === 'edit') return 'recurring-edit-title'
  return 'recurring-manage-title'
})

const recurringModalClass = computed(() =>
  historyDonation.value
    ? 'sa-donation-detail-modal sa-recurring-modal sa-donation-detail-modal--framed'
    : 'sa-donation-detail-modal sa-recurring-modal'
)

watch(
  () => isSubmitting.value || historyDetailBusy.value,
  (busy) => {
    if (props.inline) emit('busy', busy)
  }
)

watch(
  titleId,
  (id) => {
    if (props.inline) emit('labelledBy', id)
  },
  { immediate: true }
)
</script>

<template>
  <DetailSurface
    :inline="inline"
    :is-open="isOpen && Boolean(series)"
    :modal-class="recurringModalClass"
    :aria-labelledby="titleId"
    :close-disabled="isSubmitting || historyDetailBusy"
    overlay-class="sa-modal-overlay--stacked"
    @close="closeRecurringDonationModal"
  >
    <template v-if="historyDonation" #header-start>
      <button
        id="recurring-history-back"
        type="button"
        class="sa-modal-back"
        :disabled="historyDetailBusy"
        @click="backFromHistoryDonation"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd"
          />
        </svg>
        {{ $t('Dashboard.RecurringDonationModal.Back') }}
      </button>
    </template>

    <DonationDetailPanel
      v-if="historyDonation"
      embedded
      :donation="historyDonation"
      @busy="historyDetailBusy = $event"
      @deleted="backFromHistoryDonation"
    />
    <template v-else-if="series">
      <!-- Read-only first: understand the schedule before changing it. -->
      <template v-if="panelView === 'overview'">
        <DonationCharityHeader
          title-id="recurring-manage-title"
          :charity="series.charity"
          :subtitle="$t('Dashboard.RecurringDonationModal.Kind')"
        />

        <dl class="sa-donation-detail-modal__facts">
          <div class="sa-detail-row">
            <dt class="sa-detail-label">
              {{ $t('Dashboard.DonationDetailModal.Amount') }}
            </dt>
            <dd class="sa-detail-value">
              {{ formatMoneyFromPence(series.amountPence) }}
            </dd>
          </div>
          <div class="sa-detail-row">
            <dt class="sa-detail-label">
              {{ $t('Dashboard.Giving.AddDonationModal.RepeatsLabel') }}
            </dt>
            <dd class="sa-detail-value">
              {{ $t(frequencyLabelKey[series.frequency]) }}
            </dd>
          </div>
          <div class="sa-detail-row">
            <dt class="sa-detail-label">
              {{ $t('Dashboard.RecurringDonationModal.Started') }}
            </dt>
            <dd class="sa-detail-value">
              {{ dateFormat(series.firstDonationDate) }}
            </dd>
          </div>
          <div
            v-if="series.status === 'active' && series.nextDonationDate"
            class="sa-detail-row"
          >
            <dt class="sa-detail-label">
              {{ $t('Dashboard.RecurringDonationModal.NextDonation') }}
            </dt>
            <dd class="sa-detail-value sa-detail-value--stacked">
              <span>{{ dateFormat(series.nextDonationDate) }}</span>
              <button
                type="button"
                class="sa-quiet-link"
                :disabled="isSubmitting"
                @click="panelView = 'skip'"
              >
                {{ $t('Dashboard.RecurringDonationModal.SkipNext') }}
              </button>
            </dd>
          </div>
          <div
            v-if="series.status === 'stopped' && series.stoppedAt"
            class="sa-detail-row"
          >
            <dt class="sa-detail-label">
              {{ $t('Dashboard.RecurringDonationModal.Stopped') }}
            </dt>
            <dd class="sa-detail-value">
              {{ dateFormat(donationIsoToUkDate(series.stoppedAt)) }}
            </dd>
          </div>
          <div
            v-if="series.status === 'stopped' && lastDonationDate"
            class="sa-detail-row"
          >
            <dt class="sa-detail-label">
              {{ $t('Dashboard.RecurringDonationModal.LastDonation') }}
            </dt>
            <dd class="sa-detail-value">{{ dateFormat(lastDonationDate) }}</dd>
          </div>
          <div v-if="series.status !== 'stopped'" class="sa-detail-row">
            <dt class="sa-detail-label">
              {{ $t('Dashboard.RecurringDonationModal.Ends') }}
            </dt>
            <dd class="sa-detail-value">{{ endsValue }}</dd>
          </div>
          <div
            v-if="series.status !== 'stopped' && finalDonationDate"
            class="sa-detail-row"
          >
            <dt class="sa-detail-label">
              {{ $t('Dashboard.RecurringDonationModal.FinalDonation') }}
            </dt>
            <dd class="sa-detail-value">{{ dateFormat(finalDonationDate) }}</dd>
          </div>
          <div class="sa-detail-row">
            <dt class="sa-detail-label">
              {{ $t('Dashboard.RecurringDonationModal.GiftAid') }}
            </dt>
            <dd
              class="sa-detail-value"
              :class="{ 'sa-detail-value--stacked': giftAidOverview.varies }"
            >
              <span>{{ giftAidFactLabel }}</span>
              <span
                v-if="giftAidOverview.varies"
                class="sa-recurring-modal__gift-aid-detail"
              >
                {{ giftAidExceptionLabel }}
              </span>
            </dd>
          </div>
          <div class="sa-detail-row">
            <dt class="sa-detail-label">
              {{ $t('Dashboard.RecurringDonationModal.Status') }}
            </dt>
            <dd class="sa-detail-value">
              <span
                class="sa-recurring-status"
                :class="`sa-recurring-status--${series.status}`"
              >
                {{ $t(statusLabelKey) }}
              </span>
            </dd>
          </div>
        </dl>

        <section
          class="sa-section sa-recurring-modal__notes"
          aria-labelledby="recurring-notes-heading"
        >
          <h3 id="recurring-notes-heading" class="sa-modal-section-heading">
            {{ $t('Dashboard.RecurringDonationModal.Notes') }}
          </h3>
          <p class="sa-donation-detail-modal__notes">{{ notesDisplay }}</p>
        </section>

        <!--
          Generated donations stay available, but the list starts closed so the
          schedule itself is what the donor sees first.
        -->
        <details
          ref="historyDetails"
          class="sa-section sa-recurring-modal__history sa-recurring-history-disclosure"
          :open="historyOpen"
        >
          <summary
            id="recurring-history-heading"
            class="sa-recurring-history-disclosure__summary"
          >
            <span class="sa-modal-section-heading sa-recurring-history-disclosure__title">
              {{ $t('Dashboard.RecurringDonationModal.HistoryHeading') }}
            </span>
            <span class="sa-recurring-history-disclosure__end">
              <span class="sa-recurring-history-disclosure__count">
                {{ historyCountLabel }}
              </span>
              <svg
                class="sa-recurring-history-disclosure__chevron"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
          </summary>
          <div class="sa-recurring-history-disclosure__panel">
            <p v-if="historyRows.length === 0" class="sa-recurring-modal__empty">
              {{ $t('Dashboard.RecurringDonationModal.HistoryEmpty') }}
            </p>
            <div v-else class="sa-recurring-history" role="list">
              <DonationListRow
                v-for="row in visibleHistory"
                :key="row.id"
                variant="occurrence"
                :donation="row"
                :open-on-click="false"
                @select="openHistoryDonation(row.id)"
              />
            </div>
            <button
              v-if="hasMoreHistory"
              type="button"
              class="sa-btn-secondary sa-btn-secondary--compact"
              @click="showMoreHistory"
            >
              {{ $t('Dashboard.RecurringDonationModal.ShowMore') }}
            </button>
          </div>
        </details>
      </template>

      <template v-else-if="panelView === 'edit'">
        <DonationCharityHeader
          title-id="recurring-edit-title"
          :charity="series.charity"
          :subtitle="$t('Dashboard.RecurringDonationModal.Kind')"
        />
        <div class="sa-donation-detail-modal__edit-context">
          <p class="sa-donation-detail-modal__scope-note">
            {{
              $t(
                series.status === 'active'
                  ? 'Dashboard.RecurringDonationModal.EditScheduleHint'
                  : 'Dashboard.RecurringDonationModal.InactiveScheduleHint'
              )
            }}
          </p>
          <p v-if="series.status !== 'active'" class="sa-recurring-notice">
            {{ $t('Dashboard.RecurringDonationModal.InactiveEditHint') }}
          </p>
        </div>

        <!-- Section: amount. -->
        <div class="sa-form-section">
          <DashboardFormField
            :label="$t('Dashboard.Giving.AddDonationModal.AmountLabel')"
            label-for="recurring-manage-amount"
            :error="fieldErrors.amount"
          >
            <template #default="{ errorId, hasError }">
              <input
                id="recurring-manage-amount"
                v-model="amount"
                class="sa-input"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                :aria-invalid="hasError || undefined"
                :aria-describedby="hasError ? errorId : undefined"
              />
            </template>
          </DashboardFormField>

          <!-- Schedule describes the same donation as the amount: one group.
               A stopped or completed series changes it through restart. -->
          <RecurringScheduleFields
            v-if="series.status === 'active'"
            id-prefix="recurring-manage"
            v-model:frequency="frequency"
            v-model:end-type="endType"
            v-model:end-date="endDate"
            v-model:occurrence-count="occurrenceCount"
            show-next-date
            :next-date="nextDate"
            :next-date-error="fieldErrors.nextDate"
            :end-date-error="fieldErrors.endDate"
            :occurrence-count-error="fieldErrors.occurrenceCount"
            @update:next-date="nextDate = $event"
          />
        </div>

        <!-- Section: Gift Aid. -->
        <div class="sa-form-section">
          <DonationGiftAidChoice
            v-model="giftAidDeclared"
            context="recurring"
            input-name="recurring-manage-gift-aid"
            yes-input-id="recurring-manage-gift-aid-yes"
            no-input-id="recurring-manage-gift-aid-no"
            :error="fieldErrors.giftAid"
          />
        </div>

        <!-- Section: notes. -->
        <div class="sa-form-section">
          <DashboardFormField
            :label="$t('Dashboard.Giving.AddDonationModal.NotesLabel')"
            label-for="recurring-manage-notes"
          >
            <template #default>
              <input
                id="recurring-manage-notes"
                v-model="notes"
                class="sa-input"
                type="text"
              />
            </template>
          </DashboardFormField>
        </div>

        <!-- Section: which donations the change applies to. -->
        <fieldset
          v-if="valuesChanged"
          class="sa-gift-aid-choice sa-form-section"
        >
          <legend class="sa-gift-aid-choice__legend">
            {{ $t('Dashboard.RecurringDonationModal.ApplyScopeLegend') }}
          </legend>
          <div class="sa-gift-aid-choice__options">
            <label
              class="sa-gift-aid-choice__option"
              :class="{
                'sa-gift-aid-choice__option--selected': applyScope === 'future',
              }"
            >
              <input
                v-model="applyScope"
                class="sa-sr-only"
                type="radio"
                name="recurring-apply-scope"
                value="future"
              />
              <span class="sa-gift-aid-choice__copy">
                <span class="sa-gift-aid-choice__title">
                  {{ $t('Dashboard.RecurringDonationModal.ApplyFuture') }}
                </span>
              </span>
            </label>
            <label
              class="sa-gift-aid-choice__option"
              :class="{
                'sa-gift-aid-choice__option--selected':
                  applyScope === 'all_eligible',
              }"
            >
              <input
                v-model="applyScope"
                class="sa-sr-only"
                type="radio"
                name="recurring-apply-scope"
                value="all_eligible"
              />
              <span class="sa-gift-aid-choice__copy">
                <span class="sa-gift-aid-choice__title">
                  {{ $t('Dashboard.RecurringDonationModal.ApplyAll') }}
                </span>
                <span class="sa-gift-aid-choice__sub">
                  {{ $t('Dashboard.RecurringDonationModal.ApplyAllHelp') }}
                </span>
              </span>
            </label>
          </div>
        </fieldset>
      </template>

      <template v-else-if="panelView === 'skip'">
        <h2 id="recurring-manage-title">
          {{ $t('Dashboard.RecurringDonationModal.SkipTitle') }}
        </h2>
        <p>
          {{
            $t('Dashboard.RecurringDonationModal.SkipConfirm', {
              date: dateFormat(series.nextDonationDate ?? ''),
            })
          }}
        </p>
      </template>

      <template v-else-if="panelView === 'stop'">
        <h2 id="recurring-manage-title">
          {{ $t('Dashboard.RecurringDonationModal.StopTitle') }}
        </h2>
        <p>{{ $t('Dashboard.RecurringDonationModal.StopConfirm') }}</p>
      </template>

      <template v-else-if="panelView === 'delete'">
        <h2 id="recurring-manage-title">
          {{ $t('Dashboard.RecurringDonationModal.DeleteTitle') }}
        </h2>
        <p>{{ deleteIntro }}</p>
      </template>

      <template v-else-if="panelView === 'restart'">
        <h2 id="recurring-restart-title">
          {{ $t('Dashboard.RecurringDonationModal.RestartTitle') }}
        </h2>
        <p>{{ $t('Dashboard.RecurringDonationModal.RestartIntro') }}</p>
        <div class="sa-form-section">
          <DashboardFormField
            :label="$t('Dashboard.Giving.AddDonationModal.AmountLabel')"
            label-for="recurring-restart-amount"
            :error="fieldErrors.restartAmount"
          >
            <template #default="{ errorId, hasError }">
              <input
                id="recurring-restart-amount"
                v-model="restartAmount"
                class="sa-input"
                type="text"
                inputmode="decimal"
                :aria-invalid="hasError || undefined"
                :aria-describedby="hasError ? errorId : undefined"
              />
            </template>
          </DashboardFormField>

          <!-- Schedule describes the same donation as the amount: one group. -->
          <RecurringScheduleFields
            id-prefix="recurring-restart"
            v-model:frequency="restartFrequency"
            v-model:end-type="restartEndType"
            v-model:end-date="restartEndDate"
            v-model:occurrence-count="restartOccurrenceCount"
            show-next-date
            :next-date="restartNextDate"
            :min-next-date="ukTodayIso()"
            :next-date-error="fieldErrors.restartNextDate"
            :end-date-error="fieldErrors.restartEndDate"
            :occurrence-count-error="fieldErrors.restartOccurrenceCount"
            @update:next-date="restartNextDate = $event"
          />
        </div>
        <!-- Section: Gift Aid. -->
        <div class="sa-form-section">
          <DonationGiftAidChoice
            v-model="restartGiftAid"
            context="recurring"
            input-name="recurring-restart-gift-aid"
            yes-input-id="recurring-restart-gift-aid-yes"
            no-input-id="recurring-restart-gift-aid-no"
            :error="fieldErrors.restartGiftAid"
          />
        </div>
        <div class="sa-form-section">
          <DashboardFormField
            :label="$t('Dashboard.Giving.AddDonationModal.NotesLabel')"
            label-for="recurring-restart-notes"
          >
            <template #default>
              <input
                id="recurring-restart-notes"
                v-model="restartNotes"
                class="sa-input"
                type="text"
              />
            </template>
          </DashboardFormField>
        </div>
      </template>
    </template>

    <template v-if="!historyDonation" #footer>
      <div v-if="panelView === 'overview'" class="sa-recurring-modal__actions">
        <button
          type="button"
          class="sa-btn-primary"
          :disabled="isSubmitting"
          @click="startEdit"
        >
          {{ $t('Dashboard.RecurringDonationModal.Edit') }}
        </button>
        <button
          v-if="series?.status === 'active'"
          type="button"
          class="sa-btn-secondary"
          :disabled="isSubmitting"
          @click="panelView = 'stop'"
        >
          {{ $t('Dashboard.RecurringDonationModal.Stop') }}
        </button>
        <button
          v-if="series?.status === 'stopped' || series?.status === 'completed'"
          type="button"
          class="sa-btn-secondary"
          :disabled="isSubmitting"
          @click="startRestart"
        >
          {{ $t('Dashboard.RecurringDonationModal.Restart') }}
        </button>
        <div class="sa-recurring-modal__delete-wrap">
          <button
            type="button"
            class="sa-btn-secondary sa-btn-secondary--danger"
            :disabled="isSubmitting"
            @click="panelView = 'delete'"
          >
            {{ $t('Dashboard.RecurringDonationModal.Delete') }}
          </button>
        </div>
      </div>
      <div v-else-if="panelView === 'edit'" class="sa-modal-actions">
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isSubmitting"
          @click="cancelEdit"
        >
          {{ $t('Buttons.Cancel') }}
        </button>
        <button
          type="button"
          class="sa-btn-primary"
          :disabled="isSubmitting"
          @click="saveChanges"
        >
          {{ $t('Dashboard.RecurringDonationModal.Save') }}
        </button>
      </div>
      <div v-else class="sa-modal-actions">
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isSubmitting"
          @click="panelView = 'overview'"
        >
          {{ $t('Buttons.Cancel') }}
        </button>
        <button
          v-if="panelView === 'skip'"
          type="button"
          class="sa-btn-primary"
          :disabled="isSubmitting"
          @click="confirmSkip"
        >
          {{ $t('Dashboard.RecurringDonationModal.SkipConfirmCta') }}
        </button>
        <button
          v-else-if="panelView === 'stop'"
          type="button"
          class="sa-btn-primary"
          :disabled="isSubmitting"
          @click="confirmStop"
        >
          {{ $t('Dashboard.RecurringDonationModal.StopConfirmCta') }}
        </button>
        <button
          v-else-if="panelView === 'restart'"
          type="button"
          class="sa-btn-primary"
          :disabled="isSubmitting"
          @click="confirmRestart"
        >
          {{ $t('Dashboard.RecurringDonationModal.RestartCta') }}
        </button>
        <button
          v-else-if="panelView === 'delete'"
          type="button"
          class="sa-btn-secondary sa-btn-secondary--danger"
          :disabled="isSubmitting"
          @click="confirmDelete"
        >
          {{ $t('Dashboard.RecurringDonationModal.DeleteConfirmCta') }}
        </button>
      </div>
    </template>
  </DetailSurface>
</template>
